import { useEffect, lazy, Suspense, useRef, useState } from "react";

import { Route, Routes, Navigate } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "./components/Config";
import { CartState } from "./context/Context";
import useStore from "./store.js";
import { adminRoutes, AdminLayout } from "./routes/adminRoutes";
import { frontendRoutes, PrivateRoutesGuard } from "./routes/FrontEndRoutes";
import { checkoutRoutes } from "./routes/checkoutRoutes";
import NotFound from "./components/admin/NotFound";
import AccessDenied from "./components/admin/AccessDenied";

import {
  installThirdPartyErrorSuppressor,
  loadThirdPartyScripts,
} from "./setupScripts";
const Login = lazy(() => import("./components/frontend/auth/Login"));
const AcvgummiesMobileTemplate = lazy(
  () => import("./components/LanderTemplate/AcvgummiesMobileTemplate"),
);

const isPrerender =
  typeof navigator !== "undefined" && navigator.userAgent === "ReactSnap";

const SlugPage = lazy(() => import("./components/admin/MasterCheckout"));
if (!isPrerender) {
  axios.defaults.baseURL = BASE_URL + "whc-custom/public/";
}

if (typeof window !== "undefined") {
  axios.defaults.headers.post["Authorization"] =
    "Bearer " + localStorage.getItem("authToken");
  axios.defaults.headers.get["Authorization"] =
    "Bearer " + localStorage.getItem("authToken");
  axios.defaults.headers.put["Authorization"] =
    "Bearer " + localStorage.getItem("authToken");
}

const renderRoutes = (routes) =>
  routes.map((route) => {
    const { children, path, element, ...rest } = route;
    return (
      <Route
        key={
          path ??
          element?.type?.displayName ??
          element?.type?.name ??
          Math.random()
        }
        path={path}
        element={element}
        {...rest}
      >
        {children && renderRoutes(children)}
      </Route>
    );
  });

function App() {
  const isLogin = useStore((state) => state.isLogin);
  const userId = useStore((state) => state.userId);
  const setExistingCartItems = useStore((state) => state.setExistingCartItems);
  const { state, dispatch } = CartState();
  // Prevent Browser Back button
  useEffect(() => {
    function disableBack() {
      window.history.forward();
    }
    window.onload = disableBack();
    window.onpageshow = function (e) {
      if (e.persisted) disableBack();
    };
  }, []);

  useEffect(() => {
    let timeoutId;
    let cleanupScripts = () => {};
    let removeErrorSuppressor = () => {};

    const startScripts = () => {
      cleanupScripts();
      removeErrorSuppressor();
      if (!isPrerender) {
        cleanupScripts = loadThirdPartyScripts();
      }
      removeErrorSuppressor = installThirdPartyErrorSuppressor();
    };

    const handleLoad = () => {
      timeoutId = window.setTimeout(startScripts, 3000);
    };

    if (typeof window !== "undefined") {
      window.addEventListener("load", handleLoad);
      if (document.readyState === "complete") {
        handleLoad();
      }
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("load", handleLoad);
      }
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      cleanupScripts();
      removeErrorSuppressor();
    };
  }, []);

  const cartLoadedRef = useRef(false);

  useEffect(() => {
    if (!isLogin || cartLoadedRef.current) {
      return;
    }

    const params =
      typeof window !== "undefined" ? window.location.href.split("/") : [];
    if (params[3] === "admin") {
      cartLoadedRef.current = true;
      return;
    }

    if (userId) {
      if (!isPrerender) {
        axios.get(`api/cart/${userId}`).then((res) => {
          if (res.data.data != null && res.data.data.length > 0) {
            setExistingCartItems(res.data.data);
          }
        });
      }
    }

    cartLoadedRef.current = true;
  }, [isLogin, userId, setExistingCartItems]);

  const routeFallback = (
    <div className="loader_new">
      <div className="three-body">
        <div className="three-body__dot"></div>
        <div className="three-body__dot"></div>
        <div className="three-body__dot"></div>
      </div>
    </div>
  );

  return (
    <div className="App">
      <Suspense fallback={routeFallback}>
        <Routes>
          <Route path="*" element={<NotFound />} />
          <Route path="/notfound" element={<NotFound />} />
          <Route
            path="/acvgummies-mobile-landing"
            element={<AcvgummiesMobileTemplate />}
          />
          <Route path="/admin/accessdenied" element={<AccessDenied />} />
          {!isPrerender && (
            <Route
              path="/:slug"
              element={
                <Suspense
                  fallback={
                    <div className="loader_new">
                      <div className="three-body">
                        <div className="three-body__dot"></div>
                        <div className="three-body__dot"></div>
                        <div className="three-body__dot"></div>
                      </div>
                    </div>
                  }
                >
                  <SlugPage />
                </Suspense>
              }
            />
          )}
          <Route path="/admin/" element={<Login replace to="/admin/login" />} />
          <Route path="/admin/" element={<AdminLayout />}>
            <Route element={<PrivateRoutesGuard />}>
              {adminRoutes.map((route) => (
                <Route
                  key={`admin-${route.path}`}
                  path={route.path}
                  element={route.element}
                />
              ))}
            </Route>
          </Route>
          {renderRoutes(frontendRoutes)}
          {checkoutRoutes.map((route) => (
            <Route
              key={`checkout-${route.path}`}
              path={route.path}
              element={route.element}
            />
          ))}
        </Routes>
      </Suspense>
    </div>
  );
}

export default App;
